/**
 * The browser implementation of LoginEnvironment.
 *
 * It reads the render model from the `#__LOGIN_STATE__` JSON script that a
 * server render injects, and falls back to the legacy `window.__*__` globals so
 * the Scala `index.vm` path keeps working untouched until the traffic move.
 *
 * Phase 0: this module is complete but dormant — nothing constructs it yet.
 * Stores and components start reading it in Phases 1 and 2.
 *
 * This file lives under `src/platform/**`, the one place allowed to touch
 * browser globals directly (see the ESLint override).
 *
 * See docs/ssr-shared-source-plan.md, sections 2.1 and 2.4.
 */
import {
  KeyValueStore,
  LoginEnvironment,
  RenderModel,
  SdkGlobalName,
  Viewport,
} from './environment';

const LOGIN_STATE_ELEMENT_ID = '__LOGIN_STATE__';

function readLoginStateScript(): Partial<RenderModel> | undefined {
  const el = document.getElementById(LOGIN_STATE_ELEMENT_ID);
  if (!el || !el.textContent) {
    return undefined;
  }
  try {
    return JSON.parse(el.textContent) as Partial<RenderModel>;
  } catch {
    return undefined;
  }
}

function renderModelFromLegacyGlobals(): RenderModel {
  return {
    locale: window.__LOCALE__,
    language: window.__LANGUAGE__,
    basename: window.__BASEURL__,
    userType: window.__USER_TYPE__,
    baseDomain: window.__BASE_DOMAIN__,
    countryCode: window.__COUNTRY_CODE__,
    isNewLogin: window.__IS_NEW_LOGIN__ === 'true',
    noIndex: undefined,
    staticsUrl: window.__STATICS_URL__,
    experiments: window.__EXPERIMENTS__ ?? {},
    currentLoggedInUserEmail: window.__CURRENT_LOGGED_IN_USER_EMAIL__,
    errorCode: window.__ERROR_CODE__,
    ssoRedirectUrl: window.__SSO_REDIRECT_URL__,
    ssoExistingWixAccountEmail: window.__SSO_EXISTING_WIX_ACCOUNT_EMAIL__,
    ssoAccountName: window.__SSO_ACCOUNT_NAME__,
    ssoAccountImage: window.__SSO_ACCOUNT_IMAGE__,
    ssoAccountId: window.__SSO_ACCOUNT_ID__,
    recaptchaEnterpriseSiteKey: window.__RECAPTCHA_ENTERPRISE_KEY__,
    recaptchaInvisibleLoginKey: window.__RECAPTCHA_INVISIBLE_KEY__,
    resetPasswordTokenErrorCode: window.__RESET_PASSWORD_TOKEN_ERROR_CODE__,
    emailVerificationErrorCode: window.__EMAIL_VERIFICATION_ERROR_CODE__,
    tokenEmail: window.__EMAIL_VERIFICATION_TOKEN_EMAIL__,
    sessionEmail: window.__EMAIL_VERIFICATION_SESSION_EMAIL__,
  };
}

function domStore(storage: Storage): KeyValueStore {
  return {
    getItem: (key) => storage.getItem(key),
    setItem: (key, value) => storage.setItem(key, value),
    removeItem: (key) => storage.removeItem(key),
  };
}

function currentViewport(): Viewport {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    screen: {
      width: window.screen.availWidth,
      height: window.screen.availHeight,
    },
  };
}

export function createBrowserEnvironment(): LoginEnvironment {
  const fromScript = readLoginStateScript();
  const renderModel: RenderModel = fromScript
    ? { ...renderModelFromLegacyGlobals(), ...fromScript }
    : renderModelFromLegacyGlobals();

  const sessionStore = domStore(window.sessionStorage);
  const localStore = domStore(window.localStorage);

  return {
    isServer: false,
    renderModel,
    get url() {
      return new URL(window.location.href);
    },
    get userAgent() {
      return window.navigator.userAgent;
    },
    get referrer() {
      return document.referrer;
    },
    get viewport() {
      return currentViewport();
    },
    get isIframe() {
      try {
        return window.self !== window.top;
      } catch {
        // Cross-origin parent access throws — that is itself an iframe.
        return true;
      }
    },
    cookie(name) {
      const match = document.cookie.match(
        new RegExp(
          '(?:^|; )' +
            name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') +
            '=([^;]*)',
        ),
      );
      return match ? decodeURIComponent(match[1]) : undefined;
    },
    sessionStorage: sessionStore,
    localStorage: localStore,
    postMessageToParent(message) {
      window.parent?.postMessage(message, '*');
    },
    navigateExternal(url, opts) {
      if (opts?.replace) {
        window.location.replace(url);
      } else {
        window.location.href = url;
      }
    },
    openPopup(url, features) {
      return window.open(url, '_blank', features);
    },
    onResize(cb) {
      window.addEventListener('resize', cb);
      return () => window.removeEventListener('resize', cb);
    },
    global<T = unknown>(name: SdkGlobalName): T | undefined {
      return (window as unknown as Record<string, unknown>)[name] as
        | T
        | undefined;
    },
  };
}
